import React from 'react';
import { motion } from 'framer-motion';
import {
  Phone,
  Mail,
  MapPin,
  Package,
  BadgeDollarSign,
  Rocket,
  ShieldCheck,
  UserCheck,
  TruckIcon,
  HeadphonesIcon,
  Target,
  Eye,
  CircleCheckBig,
  Globe,
  Boxes,
  PhoneCall
} from 'lucide-react';

const WhatsAppIcon = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const About: React.FC = () => {
  const whyChooseUs = [
    {
      icon: <BadgeDollarSign />,
      title: 'Competitive Prices',
      description: 'We offer the most affordable international courier rates from Chandigarh without compromising on service quality.'
    },
    {
      icon: <Rocket />,
      title: 'Fast Delivery',
      description: 'Delivery in 3–7 business days to most countries. Express options available for urgent shipments.'
    },
    {
      icon: <ShieldCheck />,
      title: 'Safe & Secure Packing',
      description: 'Professional packing with high-quality materials to ensure your items reach safely, no matter the destination.'
    },
    {
      icon: <UserCheck />,
      title: 'Experienced Team',
      description: 'Our logistics experts have years of experience handling international shipments and customs clearance.'
    },
    {
      icon: <TruckIcon />,
      title: 'Free Doorstep Pickup',
      description: 'Free pickup from your home or office in Chandigarh, Mohali, Panchkula, and Zirakpur at your preferred time.'
    },
    {
      icon: <HeadphonesIcon />,
      title: 'Customer Support',
      description: 'Dedicated support via phone, WhatsApp, and email. We keep you updated at every step of your shipment.'
    }
  ];

  const howItWorks = [
    {
      step: '01',
      title: 'Contact Us',
      description: 'Call, WhatsApp, or fill our enquiry form with your shipment details. We\'ll provide an instant quote with the best rates.',
      icon: <PhoneCall />
    },
    {
      step: '02',
      title: 'Pickup from Your Location',
      description: 'Our executive visits your doorstep at your preferred time for free pickup across Chandigarh, Mohali, Panchkula & Zirakpur.',
      icon: <MapPin />
    },
    {
      step: '03',
      title: 'Packing & Customs Clearance',
      description: 'We professionally pack your items, prepare all required documentation, and handle complete customs clearance.',
      icon: <Boxes />
    },
    {
      step: '04',
      title: 'Fast International Delivery',
      description: 'Your shipment is dispatched via trusted carriers with real-time tracking until it reaches the destination safely.',
      icon: <Globe />
    }
  ];

  const services = [
    {
      title: 'Document Courier',
      description: 'Send important documents like certificates, legal papers, and official records safely to any country worldwide.'
    },
    {
      title: 'Parcel Courier',
      description: 'Ship parcels of any size internationally — from small packages to large boxes — with secure handling and tracking.'
    },
    {
      title: 'Medicine Courier',
      description: 'Reliable international medicine delivery with proper documentation, temperature care, and prescription compliance.'
    },
    {
      title: 'Food Items Courier',
      description: 'Send homemade food, pickles, spices, and dry snacks to your loved ones abroad with safe and hygienic packing.'
    },
    {
      title: 'Commercial Shipment',
      description: 'Complete B2B international shipping solutions with customs documentation, invoicing, and bulk shipping rates.'
    },
    {
      title: 'Excess Baggage',
      description: 'Travelling abroad? Ship your extra luggage at a fraction of airline excess baggage charges. Door-to-door delivery.'
    }
  ];

  return (
    <div className="about-page">
      {/* Hero Section */}
      <section className="page-hero">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="hero-content"
          >
            <h1>About Pack N Fly</h1>
            <p>Your Trusted International Courier Partner from Chandigarh to Worldwide</p>
          </motion.div>
        </div>
      </section>

      {/* Company Overview */}
      <section className="company-overview">
        <div className="container">
          <div className="overview-content">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="overview-text"
            >
              <h2>Who We Are</h2>
              <p>
                Pack N Fly is a trusted international courier and logistics company based in the Chandigarh tri-city area. We specialize in fast, safe, and affordable international shipping of documents, parcels, medicines, food items, commercial shipments, and excess baggage to countries worldwide.
              </p>
              <p>
                We understand that sending something abroad is more than just shipping — it's about trust. Whether it's important documents for your studies, medicines for your family, or a parcel of homemade food for your loved ones, we handle every shipment with personal care and responsibility.
              </p>
              <p>
                With free doorstep pickup from Chandigarh, Mohali, Panchkula, and Zirakpur, professional packing, complete customs clearance, and real-time tracking — Pack N Fly makes international shipping simple, affordable, and stress-free.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="overview-stats"
            >
              <div className="stat-item">
                <div className="stat-number">50+</div>
                <div className="stat-label">Countries Served</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">1000+</div>
                <div className="stat-label">Shipments Delivered</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">4</div>
                <div className="stat-label">Pickup Cities</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">24/7</div>
                <div className="stat-label">WhatsApp Support</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="mission-vision">
        <div className="container">
          <div className="mission-vision-grid">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="mission-card"
            >
              <Target className="card-icon" />
              <h3>Our Mission</h3>
              <p>
                To make international courier services accessible, affordable, and hassle-free for every individual and business in the Chandigarh tri-city region. We aim to bridge the distance between you and your loved ones abroad through reliable and timely deliveries.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="vision-card"
            >
              <Eye className="card-icon" />
              <h3>Our Vision</h3>
              <p>
                To become the most trusted and preferred international courier service in North India, known for competitive pricing, fast delivery, safe handling, and exceptional customer care — making global shipping as easy as local delivery.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="company-values">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="section-header"
          >
            <h2>Why Choose Pack N Fly</h2>
            <p>What makes us the preferred international courier service in Chandigarh</p>
          </motion.div>

          <div className="values-grid">
            {whyChooseUs.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="value-card"
              >
                <div className="value-icon">
                  {item.icon}
                </div>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="company-timeline">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="section-header"
          >
            <h2>How It Works</h2>
            <p>Sending an international courier is easy with Pack N Fly</p>
          </motion.div>

          <div className="timeline">
            {howItWorks.map((step, index) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className={`timeline-item ${index % 2 === 0 ? 'left' : 'right'}`}
              >
                <div className="timeline-content">
                  <div className="timeline-year">{step.step}</div>
                  <h3>{step.title}</h3>
                  <p>{step.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Services */}
      <section className="leadership-team">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="section-header"
          >
            <h2>Our Services</h2>
            <p>Comprehensive international courier solutions for every need</p>
          </motion.div>

          <div className="team-grid">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="team-member"
              >
                <div className="member-info">
                  <h3>{service.title}</h3>
                  <p>{service.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Local Area SEO Section */}
      <section className="company-overview">
        <div className="container">
          <div className="overview-content">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="overview-text"
            >
              <h2>International Courier Service in Chandigarh</h2>
              <p>
                Looking for a reliable international courier service in Chandigarh? Pack N Fly is your go-to partner for sending documents, parcels, medicines, food items, and commercial shipments from Chandigarh to anywhere in the world.
              </p>
              <p>
                We provide free doorstep pickup across Chandigarh, Mohali, Panchkula, Zirakpur, and nearby areas. Our team handles everything from professional packing to customs documentation, so you don't have to worry about a thing. Just call or WhatsApp us, and we'll take care of the rest.
              </p>
              <p>
                With competitive pricing, fast delivery timelines (3–7 business days), real-time tracking, and dedicated customer support, Pack N Fly has become the preferred choice for international courier in the tri-city area. Whether you're a student sending documents abroad, a family sending food to loved ones, or a business shipping commercial goods — we've got you covered.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="local-info-sidebar"
            >
              <div className="local-info-card">
                <h3><MapPin size={20} /> Free Pickup Areas</h3>
                <ul>
                  <li>Chandigarh</li>
                  <li>Mohali</li>
                  <li>Panchkula</li>
                  <li>Zirakpur</li>
                  <li>Nearby Areas</li>
                </ul>
              </div>
              <div className="local-info-card">
                <h3><Package size={20} /> What You Can Send</h3>
                <ul>
                  <li>Documents & Certificates</li>
                  <li>Parcels & Packages</li>
                  <li>Medicines (with prescription)</li>
                  <li>Food Items & Spices</li>
                  <li>Commercial Goods</li>
                  <li>Excess Baggage</li>
                </ul>
              </div>
              <div className="local-info-card highlight-card">
                <h3><Phone size={20} /> Get a Free Quote</h3>
                <p>Call or WhatsApp us now</p>
                <a href="tel:+918847428801" className="local-cta-link">+91 8847428801</a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Trust Points */}
      <section className="mission-vision">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="section-header"
          >
            <h2>What You Get with Pack N Fly</h2>
          </motion.div>
          <div className="mission-vision-grid">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="mission-card"
            >
              <CircleCheckBig className="card-icon" />
              <h3>Complete Transparency</h3>
              <p>No hidden charges. You get a clear quote upfront with full cost breakdown. Track your shipment in real-time from pickup to delivery.</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="vision-card"
            >
              <ShieldCheck className="card-icon" />
              <h3>Safe & Insured</h3>
              <p>Professional packing with quality materials. Every shipment is handled with care and tracked at each step to ensure safe delivery.</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="services-cta">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="cta-content"
          >
            <h2>Ready to Ship Internationally?</h2>
            <p>Get a free quote now — call, WhatsApp, or email us today</p>
            <div className="cta-buttons">
              <a href="tel:+918847428801" className="btn btn-primary">
                Call: +91 8847428801
                <Phone />
              </a>
              <a
                href="https://wa.me/918847428801?text=Hi%2C%20I%20need%20a%20quote%20for%20international%20courier%20from%20Chandigarh."
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-whatsapp"
              >
                <WhatsAppIcon size={18} /> WhatsApp Now
              </a>
              <a href="mailto:packnfly96@gmail.com" className="btn btn-secondary">
                Send Email
                <Mail />
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;
